/**
 * The steps between a talk (or notes) and the board, on a phone and an iPad: one status screen while the room works,
 * and a short list to check only where a check is real (the names, the chapters). The seven steps and what each waits
 * for are the computer's, from `BoardView`; the page designer is not asked, the first page it writes is used.
 */
import { useEffect, useRef } from "react";
import type { SceneStep } from "../../../shared/board-copy";
import { liveLongLabel } from "../../../shared/live-time";
import type { BoardState, BoardStateResponse, LibraryAsset } from "../../../shared/types";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import type { SeasonLayout } from "../../components/season/layout";
import { chapterCount, chapterRowsOf, type ChapterRow, useChapterRows } from "../make/MakeChapters";
import { SCENE_NAMES, useNamesCheck } from "../make/MakeSteps";
import { mmss } from "../make/time";
import { type BoardPhase, BOARD_STEPS } from "../progress";
import { overallPercent, stepShare, useAutoDesign, workLine, workStepIndex, workTitle } from "./makeWork";
import { SeasonMakeFrame } from "./SeasonMakeFrame";
import { SeasonTalkStep } from "./SeasonStart";

const ROOMS = { to: "/rooms", label: "Rooms" };
/** A beat to see the page written before the design starts on its own. */
const SEE_PAGE_MS = 1200;

export interface MakeAction {
  label: string;
  onClick: () => void;
}

export interface SeasonMakeProps {
  layout: SeasonLayout;
  phase: Exclude<BoardPhase, "board">;
  live: boolean;
  webinarId: string;
  server: BoardStateResponse | null;
  durationSec: number | null;
  /** "10 new video hours added for $39.", when the upload needed a top-up. */
  receipt: string;
  /** Why the step stopped, in the server's words, and what the host can press. */
  error: string;
  action?: MakeAction;
  second?: MakeAction;
  /** The board the read wrote, once it has landed, and what to do when it has been seen. */
  built: BoardState | null;
  onBuilt: () => void;
  entryBusy: boolean;
  onTalk: (talk: LibraryAsset) => void;
  onFinishLater: () => void;
  onNamesChecked: () => void;
  onChapters: (rows: ChapterRow[], version: number) => void;
  /** The design is being asked for (the chapters were sent). */
  designing: boolean;
  onChosen: () => void;
}

function Heard({ lines }: { lines: ReadonlyArray<{ ts: string; said: string }> }) {
  return (
    <div className="mk-heard" aria-live="polite">
      {lines.slice(-3).map((l) => (
        <p key={`${l.ts}-${l.said}`}>
          <span>{l.ts}</span>
          {l.said}
        </p>
      ))}
    </div>
  );
}

/** What the read has finished so far, in its own words, newest last. */
function Landed({ steps }: { steps: readonly SceneStep[] }) {
  return (
    <ul className="mk-landed" aria-live="polite">
      {steps.map((s) => (
        <li key={s}>
          <Icon name="check" />
          {SCENE_NAMES[s]}
        </li>
      ))}
    </ul>
  );
}

const WAVE_BARS = 48;
const wave = Array.from({ length: WAVE_BARS }, (_, i) => 18 + Math.round(Math.abs(Math.sin(i * 1.7) * 26 + Math.sin(i * 0.6) * 14)));

/** His own frames: the talk's poster, with the wave of what has been heard drawn over it while it listens. */
function Frames({ poster, heard }: { poster: string | null; heard: number | null }) {
  return (
    <span className="k-fr k-fr-lg mk-poster">
      {poster ? <img src={poster} alt="" /> : <Icon name="video" />}
      {heard !== null ? (
        <span className="mk-wave" aria-hidden="true">
          {wave.map((h, i) => (
            <i key={i} className={i / WAVE_BARS <= heard ? "on" : ""} style={{ height: `${h}%` }} />
          ))}
        </span>
      ) : null}
    </span>
  );
}

/** A live's page filling in: grey lines until the page is written. */
function MiniPage({ built }: { built: BoardState | null }) {
  const title = built?.page.find((c) => c.kind === "title");
  const desc = built?.page.find((c) => c.kind === "desc");
  const cover = built?.page.find((c) => c.kind === "chapters");
  const sk = (n: number) => Array.from({ length: n }, (_, i) => <i key={i} className="mk-sk" />);
  return (
    <div className="mk-mini">
      {title && title.kind === "title" ? <h3>{title.v}</h3> : sk(2)}
      {desc && desc.kind === "desc" ? <p>{desc.v}</p> : sk(3)}
      <div className="mk-cover">
        <b>What we will cover</b>
        {cover && cover.kind === "chapters"
          ? cover.rows.map((r) => (
              <span key={`${r.t}-${r.title}`}>
                <i>
                  <Icon name="check" />
                </i>
                {r.title}
              </span>
            ))
          : sk(4)}
      </div>
    </div>
  );
}

export function SeasonMake(p: SeasonMakeProps) {
  const { layout, phase, live, webinarId, server } = p;
  const kind = live ? "live" : "rec";
  const total = live ? 4 : 7;
  const designPhase = phase === "design" || p.designing;
  const autoDesign = useAutoDesign(webinarId, designPhase, p.onChosen);

  // The read has landed: the board is taken a moment later, so a live's page is seen written.
  const built = useRef(p.onBuilt);
  built.current = p.onBuilt;
  const landed = p.built !== null && (phase === "build" || phase === "write");
  useEffect(() => {
    if (!landed) return;
    const t = window.setTimeout(() => built.current(), live ? SEE_PAGE_MS : 0);
    return () => window.clearTimeout(t);
  }, [landed, live]);

  const back = ROOMS;
  const later = p.onFinishLater;
  const lines = server?.heardLines ?? [];

  if (phase === "entry") {
    return <SeasonTalkStep layout={layout} starting={p.entryBusy} error={p.error} onMake={p.onTalk} back={back} onFinishLater={later} />;
  }
  if (phase === "names") return <SeasonNames {...p} />;
  if (phase === "chapters" && !p.designing) return <SeasonChapters {...p} />;

  const shown: Exclude<BoardPhase, "board"> = designPhase ? "design" : phase;
  const index = workStepIndex(shown, live);
  const percent = overallPercent(shown, live, server);
  const error = p.error || autoDesign.error;
  const action = autoDesign.error && !p.error ? { label: "Try again", onClick: autoDesign.retry } : p.action;
  return (
    <SeasonMakeFrame
      layout={layout}
      kind={kind}
      current={index}
      reached={index}
      kicker={`Step ${index + 1} of ${total}`}
      title={workTitle(shown, server)}
      back={back}
      onFinishLater={later}
    >
      <div className="mk-prog" role="progressbar" aria-label="Progress" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <i style={{ width: `${percent}%` }} />
      </div>
      <p className="mk-line" aria-live="polite">
        {workLine(shown, server, p.durationSec)}
      </p>
      {p.receipt ? <p className="note good">{p.receipt}</p> : null}
      {live ? (
        <MiniPage built={p.built} />
      ) : shown === "playable" ? (
        <div className="mk-cells" aria-hidden="true">
          {Array.from({ length: Math.max(server?.video.chunkCount ?? 1, 1) }, (_, i) => (
            <i key={i} className={i < (server?.video.chunksDone ?? 0) ? "on" : ""} />
          ))}
        </div>
      ) : (
        <Frames poster={server?.video.posterUrl ?? null} heard={shown === "listen" ? stepShare("listen", false, server) : null} />
      )}
      {shown === "listen" && lines.length ? <Heard lines={lines} /> : null}
      {shown === "build" ? <Landed steps={server?.readProgress ?? []} /> : null}
      {error ? (
        <div className="note warn" role="alert">
          <Icon name="info" />
          <div>
            <span>{error}</span>
            {action || p.second ? (
              <div className="row g8 mt8">
                {action ? (
                  <Button size="s" onClick={action.onClick}>
                    {action.label}
                  </Button>
                ) : null}
                {p.second ? (
                  <Button size="s" variant="q" onClick={p.second.onClick}>
                    {p.second.label}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </SeasonMakeFrame>
  );
}

/** Did we hear these names right: short, a field for each. */
function SeasonNames(p: SeasonMakeProps) {
  const { names, said, setSaid, sending, error, confirm } = useNamesCheck(p.webinarId, p.onNamesChecked);
  const index = BOARD_STEPS.findIndex((s) => s.key === "names");
  return (
    <SeasonMakeFrame
      layout={p.layout}
      kind="rec"
      current={index}
      reached={index}
      kicker={`Step ${index + 1} of 7`}
      title="Did we hear these names right?"
      back={ROOMS}
      onFinishLater={p.onFinishLater}
      action={
        names ? (
          <Button variant="p" size="lg" full={p.layout === "phone"} after="right" disabled={sending} onClick={() => void confirm()}>
            {sending ? "Saving the names…" : "Looks right, build the board"}
          </Button>
        ) : null
      }
    >
      {names ? (
        <div className="mk-names">
          {names.map((n, i) => {
            const first = n.mentions[0];
            return (
              <div className="mk-name" key={n.name}>
                <div>
                  <b>{n.name}</b>
                  <span>
                    {n.count} {n.count === 1 ? "mention" : "mentions"}
                    {first ? ` · heard at ${first.ts}` : ""}
                  </span>
                </div>
                <div className="field">
                  <label htmlFor={`mkn${i}`}>It should say</label>
                  <input dir="auto" id={`mkn${i}`} className="inp" value={said[n.name] ?? n.name} onChange={(e) => setSaid((s) => ({ ...s, [n.name]: e.target.value }))} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mk-load" aria-busy="true" />
      )}
      {error ? (
        <p className="note warn" role="alert">
          <Icon name="info" />
          <span>{error}</span>
        </p>
      ) : null}
    </SeasonMakeFrame>
  );
}

/** The chapters, as the read found them: each one's name, in a short list. Splitting and joining stay on a computer. */
function SeasonChapters(p: SeasonMakeProps) {
  const { board, rows, setRows } = useChapterRows(p.webinarId);
  const index = BOARD_STEPS.findIndex((s) => s.key === "chapters");
  const duration = p.durationSec ?? 0;
  return (
    <SeasonMakeFrame
      layout={p.layout}
      kind="rec"
      current={index}
      reached={index}
      kicker={`Step ${index + 1} of 7`}
      title={board ? chapterCount(rows, duration) : "The chapters"}
      back={ROOMS}
      onFinishLater={p.onFinishLater}
      action={
        board ? (
          <Button variant="p" size="lg" full={p.layout === "phone"} after="right" disabled={rows.length === 0} onClick={() => p.onChapters(rows, board.version)}>
            Design the page
          </Button>
        ) : null
      }
    >
      {board ? (
        <>
          <ul className="k-gl k-nolead mk-chaps">
            {rows.map((c, i) => (
              <li className="k-lr" key={`${c.t}-${i}`}>
                <div className="k-lr-b">
                  <span className="mk-ct">{mmss(c.t)}</span>
                  <input dir="auto" className="inp" value={c.title} aria-label={`Chapter ${i + 1} title`} onChange={(e) => setRows((r) => r.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} />
                </div>
              </li>
            ))}
          </ul>
          <p className="mk-bigscr">
            <Icon name="monitor" /> Edit on a bigger screen
          </p>
        </>
      ) : (
        <div className="mk-load" aria-busy="true" />
      )}
      {p.error ? (
        <p className="note warn" role="alert">
          <Icon name="info" />
          <span>{p.error}</span>
        </p>
      ) : null}
    </SeasonMakeFrame>
  );
}

/** The board is made: the poster, what it holds in one line, and Walk it through. */
export function SeasonReady({ layout, live, server, state, durationSec, onWalk }: { layout: SeasonLayout; live: boolean; server: BoardStateResponse; state: BoardState; durationSec: number | null; onWalk: () => void }) {
  const kind = live ? "live" : "rec";
  const moments = state.room.filter((c) => c.kind === "poll" || c.kind === "question" || c.kind === "reaction" || c.kind === "handout").length;
  const chapters = chapterRowsOf(state).length;
  const minutes = durationSec ? Math.max(1, Math.round(durationSec / 60)) : 0;
  const line = live
    ? server.live
      ? liveLongLabel(server.live.startsAt, server.live.timezone)
      : ""
    : [minutes ? `${minutes} minutes` : "", chapters ? `${chapters} ${chapters === 1 ? "chapter" : "chapters"}` : "", moments ? `${moments} ${moments === 1 ? "moment" : "moments"} and the offer` : ""].filter(Boolean).join(" · ");
  const total = live ? 4 : 7;
  return (
    <SeasonMakeFrame
      layout={layout}
      kind={kind}
      current={total}
      reached={total}
      title={live ? "Your page is written" : "Your board is built"}
      back={ROOMS}
      action={
        <Button variant="p" size="lg" full={layout === "phone"} after="right" onClick={onWalk}>
          Walk it through
        </Button>
      }
    >
      <Frames poster={server.video.posterUrl ?? null} heard={null} />
      <div className="mk-ready">
        <span className="mk-bigck">
          <Icon name="check" />
        </span>
        <div>
          <b>{server.title || state.workingTitle}</b>
          <span>{line}</span>
        </div>
      </div>
    </SeasonMakeFrame>
  );
}
